import { observer } from 'src/utils/mobx-react';
import React, { useState } from 'react';

import JsonView from '@microlink/react-json-view';

import { Dialog, DialogTitle } from 'src/domains/common/components/dialog/dialog';
import moment from 'moment';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Row } from '@tanstack/react-table';

import { Button } from 'src/domains/common/components/button/button';
import { SuccessResponseType } from 'src_server/types/paymentSearch/paymentsSearchTypes';
import { useAppStateContext } from 'src/AppState/AppState';

interface ExpandedRowPropsType {
    row: Row<SuccessResponseType['results'][number]>;
}

export const ExpandedRow = observer('ExpandedRow', ({ row }: ExpandedRowPropsType) => {
    const [openModal, setOpenModal] = useState(false);
    const { paymentSearchWalletAddress } = useAppStateContext().configState.config;
    const { paymentResponse, logs } = row.original;
    const { createdAt, finalizedAt, updatedAt, assignee, displayedReason, number, id, subtype, expires, provider } =
        row.original.paymentDetails;

    let realexLog = null;
    let additionalContent = null;

    if (assignee !== undefined) {
        additionalContent = (
            <div className='flex flex-col gap-1'>
                <span className='mb-3'>Manual Adjustment Details</span>
                <div className='flex gap-2'>
                    <div className='flex gap-1 flex-col text-muted-foreground text-xs'>
                        <span>Assignee</span>
                    </div>
                    <div className='flex gap-1 flex-col text-xs'>
                        <span>{`${assignee.name} (ID: ${assignee.id})`}</span>
                    </div>
                </div>
            </div>
        );
    } else if (provider === 'realex') {
        additionalContent = (
            <div className='flex flex-col gap-1'>
                <span className='mb-3'>Card Transaction Details</span>
                <div className='flex gap-2'>
                    <div className='flex gap-1 flex-col text-muted-foreground text-xs'>
                        <span>Card Brand</span>
                        <span>Provider Reference ID</span>
                        <span>Expiry Date</span>
                    </div>
                    <div className='flex gap-1 flex-col text-xs'>
                        <span>{subtype ?? '--'}</span>
                        <span>{id ?? '--'}</span>
                        <span>{expires ?? '--'}</span>
                    </div>
                </div>
            </div>
        );
    }

    if (logs !== undefined && logs !== null) {
        const realexLogs = logs.map((log, idx) => {
            const logEntryFields = Object.keys(log.data);
            return (
                <div
                    key={idx}
                    className='flex flex-col gap-1'
                >
                    <div className='flex gap-2'>
                        <div className='flex gap-1 flex-col text-muted-foreground text-xs'>
                            {logEntryFields.map((entry, idx) => (
                                <span key={idx}>{entry}</span>
                            ))}
                        </div>
                        <div className='flex gap-1 flex-col text-xs'>
                            {logEntryFields.map((entry, idx) => (
                                <span key={idx}>{log.data[entry]}</span>
                            ))}
                        </div>
                    </div>

                    <div className='flex gap-2'>
                        <div className='flex gap-1 flex-col text-muted-foreground text-xs'>
                            <span>Date</span>
                            <span>Status Change</span>
                        </div>
                        <div className='flex gap-1 flex-col text-xs'>
                            <span>{moment(log.date).format('DD/MM/YYYY HH:mm')}</span>
                            <span>
                                {log.oldStatus} {'->'} {log.newStatus}
                            </span>
                        </div>
                    </div>
                </div>
            );
        });
        realexLog = (
            <div className='flex flex-col gap-1'>
                <span className='mb-3'>Realex Logs</span>
                {realexLogs}
            </div>
        );
    }

    return (
        <div className='flex gap-16 my-3'>
            <div className='flex flex-col gap-1'>
                <span className='mb-3'>Transaction Details</span>
                <div className='flex gap-2'>
                    <div className='flex gap-1 flex-col text-muted-foreground text-xs'>
                        <span>Create date</span>
                        <span>Finalize date</span>
                        <span>Update date</span>
                        {paymentResponse?.providerOrderId !== undefined &&
                            paymentResponse.providerOrderId !== null &&
                            paymentResponse.providerOrderId !== '' && <span>Order ID</span>}
                        {paymentResponse?.providerAuthCode !== undefined &&
                            paymentResponse.providerAuthCode !== null &&
                            paymentResponse.providerAuthCode !== '' && <span>Auth Code</span>}
                        {number !== undefined && paymentSearchWalletAddress && <span>Wallet Address</span>}
                    </div>
                    <div className='flex gap-1 flex-col text-xs'>
                        <span>{moment(createdAt).format('DD/MM/YYYY HH:mm')}</span>
                        <span>
                            {finalizedAt === null ? 'Invalid Date' : moment(finalizedAt).format('DD/MM/YYYY HH:mm')}
                        </span>
                        <span>{moment(updatedAt).format('DD/MM/YYYY HH:mm')}</span>
                        {paymentResponse?.providerOrderId !== undefined &&
                            paymentResponse.providerOrderId !== null &&
                            paymentResponse.providerOrderId !== '' && <span>{paymentResponse.providerOrderId}</span>}
                        {paymentResponse?.providerAuthCode !== undefined &&
                            paymentResponse.providerAuthCode !== null &&
                            paymentResponse.providerAuthCode !== '' && <span>{paymentResponse.providerAuthCode}</span>}
                        {number !== undefined && paymentSearchWalletAddress && <span>{number}</span>}
                    </div>
                </div>
                {paymentResponse !== null && (
                    <Button
                        className='underline w-fit self-start px-0'
                        variant='ghost'
                        onClick={() => setOpenModal(true)}
                    >
                        More Details
                    </Button>
                )}
            </div>
            {displayedReason !== undefined && (
                <div className='flex flex-col gap-1'>
                    <span className='mb-3'>Payment Description</span>
                    <span className='flex text-xs'>{displayedReason}</span>
                </div>
            )}
            {additionalContent}
            {realexLog}

            {openModal === true ? (
                <Dialog
                    onClickOutside={() => setOpenModal(false)}
                    className='overflow-scroll max-h-[calc(100%-40px)] max-w-[calc(100%-40px)] w-full rounded-none space-y-2'
                >
                    <div className='flex justify-between'>
                        <DialogTitle>Payment Details:</DialogTitle>
                        <IconButton
                            icon='x'
                            variant='ghost'
                            onClick={() => setOpenModal(false)}
                        />
                    </div>

                    <JsonView
                        src={paymentResponse ?? {}}
                        displayDataTypes={false}
                        collapsed={6}
                        name={null}
                        groupArraysAfterLength={1000}
                        style={{ fontSize: '14px' }}
                    />
                </Dialog>
            ) : null}
        </div>
    );
});
